/**
 * In-flight request tracker for the global progress bar.
 *
 * Each active fetch is recorded with its start time and an OPTIONAL expected
 * duration (looked up via a pluggable estimator — see lib/eta.ts). GlobalProgress
 * subscribes and, when an expected duration is known, drives a real
 * time-proportional bar + ETA countdown; otherwise it falls back to the classic
 * indeterminate easing. The fetch wrapper's contract is unchanged: it still
 * returns the original promise/response and propagates errors.
 */

export type InFlight = {
  id: number;
  /** monotonic ms (see now()) when the request started */
  startedAt: number;
  /** predicted duration in ms, or null when we have no history for it */
  expectedMs: number | null;
};

type Estimator = (method: string, url: string) => number | null;

let seq = 0;
const active = new Map<number, InFlight>();
const listeners = new Set<() => void>();
let estimator: Estimator | null = null;
let _installed = false;

/** Monotonic clock in ms; shared so elapsed = now() - startedAt is consistent. */
export function now(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function notify() {
  listeners.forEach((fn) => fn());
}

/** Inject the ETA lookup (lib/eta wires this). null disables predictions. */
export function setEstimator(fn: Estimator | null) {
  estimator = fn;
}

/** Register a starting request; returns an id to pass to end(). */
export function begin(method: string, url: string): number {
  const id = ++seq;
  let expectedMs: number | null = null;
  try {
    expectedMs = estimator ? estimator(method, url) : null;
  } catch {
    expectedMs = null;
  }
  active.set(id, { id, startedAt: now(), expectedMs });
  notify();
  return id;
}

/** Mark a request finished. */
export function end(id: number) {
  if (active.delete(id)) notify();
}

/** Snapshot of the current in-flight requests. */
export function snapshot(): InFlight[] {
  return Array.from(active.values());
}

/** Count of in-flight requests. */
export function inflightCount(): number {
  return active.size;
}

/** Subscribe to begin/end changes. Returns an unsubscribe function. */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function requestInfo(args: Parameters<typeof fetch>): { method: string; url: string } {
  const [input, init] = args;
  let url = '';
  let method = 'GET';
  if (typeof input === 'string') {
    url = input;
  } else if (input instanceof URL) {
    url = input.toString();
  } else if (input && typeof (input as Request).url === 'string') {
    url = (input as Request).url;
    method = (input as Request).method || 'GET';
  }
  if (init && init.method) method = init.method;
  return { method: method.toUpperCase(), url };
}

/** Requests that must NOT drive the bar (meta polling that would flash it). */
function ignored(url: string): boolean {
  return url.includes('/api/eta');
}

// Guard: only patch window.fetch once.
/**
 * Wraps window.fetch to record in-flight requests. Call once from main.tsx.
 * The wrapper is transparent — same arguments, same returned promise, errors
 * propagate — it only brackets the call with begin()/end().
 */
export function installFetchProgress() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  const original = window.fetch.bind(window);
  window.fetch = function patchedFetch(...args: Parameters<typeof fetch>) {
    const { method, url } = requestInfo(args);
    if (ignored(url)) return original(...args);
    const id = begin(method, url);
    return original(...args).finally(() => {
      end(id);
    });
  };
}

/** Exposed only for tests — resets module state. */
export function _reset() {
  active.clear();
  listeners.clear();
  estimator = null;
  _installed = false;
  seq = 0;
}
